<template>
  <section class="admin-content" id="contact-search">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3">
                <i class="fe fe-bar-chart-2" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">Claims Statistics</h4>
                <small style="opacity: 0.8;">Provider-level claims report and export</small>
              </div>
            </div>
            <div class="d-flex align-items-center" style="gap: 8px;">
              <TodayTasks />
              <router-link v-if="user && user.job_title == 'Claims Verifier'" to="/state-dashboard" class="btn filter-toggle-btn">
                <i class="fe fe-home mr-1"></i> State Dashboard
              </router-link>
              <button class="btn filter-toggle-btn" @click="filterparams = !filterparams">
                <i class="fe mr-1" :class="filterparams ? 'fe-x' : 'fe-filter'"></i>
                {{ filterparams ? 'Close Filter' : 'Filter' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">

          <!-- ── Collapsible Filter Card ── -->
          <div v-show="filterparams" class="card shadow-sm form-card mb-4">
            <div class="form-card-header">
              <div class="section-number">
                <i class="fe fe-filter" style="font-size: 12px;"></i>
              </div>
              <h6 class="mb-0 font-weight-bold text-white">Filter Parameters</h6>
            </div>
            <div class="card-body px-4 py-4">
              <div class="form-row">

                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Sector</label>
                  <select class="form-control" v-model="sector">
                    <option value="">All Sectors</option>
                    <option :value="sec" v-for="sec in sectors" :key="sec">{{ sec }}</option>
                  </select>
                </div>

                <div class="form-group col-md-4"
                  v-if="user && (user.type == 'shis' || user.type == 'employee')">
                  <label class="small font-weight-bold text-secondary">Health Facility</label>
                  <v-select v-model="provider_id" label="agency_name" :options="providers"
                    @change="getClaims('json')" placeholder="All facilities…" />
                  <button @click="getProviders" class="btn btn-sm btn-outline-secondary mt-1">
                    <i class="fe fe-refresh-cw mr-1"></i> All ({{ providers.length }})
                  </button>
                </div>

                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Status</label>
                  <select class="form-control" v-model="claim_status">
                    <option value="">All</option>
                    <option value="approved">Approved</option>
                    <option value="vetted">Vetted</option>
                    <option value="verified">Verified</option>
                    <option value="forwarded">Forwarded to ES</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div class="form-group col-md-3">
                  <label class="small font-weight-bold text-secondary">Level of Care</label>
                  <select class="form-control" v-model="phc_type">
                    <option value="">All</option>
                    <option v-for="t in careLevels" :key="t" :value="t">{{ t }}</option>
                  </select>
                </div>

                <div class="form-group col-md-3">
                  <label class="small font-weight-bold text-secondary">Ownership</label>
                  <select class="form-control" v-model="phc_general">
                    <option value="">All</option>
                    <option value="Public">Public</option>
                    <option value="Private">Private</option>
                  </select>
                </div>

                <div class="form-group col-md-3">
                  <label class="small font-weight-bold text-secondary">
                    <i class="fe fe-calendar mr-1"></i> Start Date
                  </label>
                  <input type="date" class="form-control" v-model="from" />
                </div>

                <div class="form-group col-md-3">
                  <label class="small font-weight-bold text-secondary">
                    <i class="fe fe-calendar mr-1"></i> End Date
                  </label>
                  <input type="date" class="form-control" v-model="to" />
                </div>

              </div>

              <div class="d-flex flex-wrap mt-2" style="gap: 8px;">
                <button class="btn btn-submit" @click="runAll">
                  <i class="fe fe-bar-chart-2 mr-1"></i> Get Statistics
                </button>
                <button class="btn btn-outline-info" @click="getClaims('csv')">
                  <i class="fe fe-download mr-1"></i> Export Claims
                </button>
                <button class="btn btn-outline-secondary">
                  <download-excel :data="providers" :fields="provider_fields" type="xls"
                    :escapeCsv="false" name="Provider_Claims_Statistics.xls">
                    <i class="fe fe-file-text mr-1"></i> Provider Excel
                  </download-excel>
                </button>
                <button class="btn btn-outline-danger" @click="clearField">
                  <i class="fe fe-x-circle mr-1"></i> Clear
                </button>
              </div>
            </div>
          </div>

          <!-- ── Summary Stat Cards ── -->
          <div class="d-flex justify-content-end mb-2">
            <div class="card-download-tools">
              <button class="btn-download" title="Download as image" @click="downloadElementImage('summaryCardsRef', 'claims-summary')">
                <i class="fe fe-image"></i>
              </button>
              <button class="btn-download" title="Download as CSV" @click="downloadSummaryCsv">
                <i class="fe fe-download"></i>
              </button>
            </div>
          </div>
          <div class="row mb-4" ref="summaryCardsRef">
            <div class="col-md-4">
              <div class="stat-card">
                <div class="stat-icon-wrap" style="background: #e8f4ff;">
                  <i class="fe fe-file-text" style="color: #2563eb;"></i>
                </div>
                <div>
                  <p class="stat-label">Total Claims</p>
                  <h3 class="stat-value">{{ lastProviderClaimsCount | numeral(0, 0) }}</h3>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="stat-card">
                <div class="stat-icon-wrap" style="background: #fff7e6;">
                  <i class="fe fe-credit-card" style="color: #d97706;"></i>
                </div>
                <div>
                  <p class="stat-label">Amount Requested</p>
                  <h3 class="stat-value">
                    ₦{{ providers[providers.length - 1].total_cost | numeral(0, 0) }}
                  </h3>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="stat-card">
                <div class="stat-icon-wrap" style="background: #ecfdf5;">
                  <i class="fe fe-check-circle" style="color: #059669;"></i>
                </div>
                <div>
                  <p class="stat-label">Amount Approved</p>
                  <h3 class="stat-value">
                    ₦{{ providers[providers.length - 1].total_verified_price | numeral(0, 0) }}
                  </h3>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Claims Trend Chart ── -->
          <div class="card shadow-sm table-card mb-4">
            <div class="form-card-header">
              <div class="section-number">
                <i class="fe fe-trending-up" style="font-size: 12px;"></i>
              </div>
              <h6 class="mb-0 font-weight-bold text-white">Claims by Date</h6>
              <small class="ml-auto text-white" style="opacity:.75;">Use the chart's ⋮ menu to download PNG / CSV</small>
            </div>
            <div class="card-body p-3">
              <ClaimsDate />
            </div>
          </div>

          <!-- ── Diagnoses Bar Chart ── -->
          <div class="card shadow-sm table-card mb-4">
            <div class="form-card-header">
              <div class="section-number">
                <i class="fe fe-thermometer" style="font-size: 12px;"></i>
              </div>
              <h6 class="mb-0 font-weight-bold text-white">Top Diagnoses</h6>
              <span v-if="diagnosisCategories.length" class="badge badge-light ml-auto" style="border-radius: 20px;">
                {{ diagnosisCategories.length }} diagnoses
              </span>
            </div>
            <div class="card-body p-3">

              <claims-diagnoses-chart
                :categories="diagnosisCategories"
                :series="diagnosisSeries"
                :loading="diagnosisChartLoading"
                :height="380"
                filename="claims-top-diagnoses"
              />

              <!-- Chart action buttons -->
              <div class="d-flex flex-wrap mt-3 pt-3" style="gap: 8px; border-top: 1px solid #f1f5f9;">
                <button class="btn btn-submit btn-sm" @click="getDiagnosesChart" :disabled="diagnosisChartLoading">
                  <i class="fe fe-refresh-cw mr-1"></i> Refresh Chart
                </button>
                <button class="btn btn-outline-warning btn-sm" @click="getDiagnosesStats">
                  <i class="fe fe-download mr-1"></i> Export Diagnoses CSV
                </button>
              </div>
            </div>
          </div>

          <!-- ── Top services and drugs (claims_services) ── -->
          <ClaimItemsStats :from="from" :to="to" :sector="sector" :provider-id="provider_id && provider_id.id"
            :claim-status="claim_status" :phc-type="phc_type" :phc-general="phc_general" :refresh-key="itemsRefresh" />

          <!-- ── Diagnoses / services / drugs / programmes with drill-down to facility and claim ── -->
          <ClaimAnalysis :from="from" :to="to" :sector="sector" :provider-id="provider_id && provider_id.id"
            :phc-type="phc_type" :phc-general="phc_general" :refresh-key="itemsRefresh" />

          <!-- ── Providers Table ── -->
          <div class="card shadow-sm table-card">
            <div class="form-card-header">
              <div class="section-number">
                <i class="fe fe-list" style="font-size: 12px;"></i>
              </div>
              <h6 class="mb-0 font-weight-bold text-white">Provider Breakdown</h6>
              <span v-if="providers && providers.length" class="badge badge-light ml-auto mr-2" style="border-radius: 20px;">
                {{ providers.length }} providers
              </span>
              <div class="card-download-tools card-download-tools--dark">
                <button class="btn-download" title="Download as image" @click="downloadElementImage('providersTableRef', 'claims-provider-breakdown')">
                  <i class="fe fe-image"></i>
                </button>
                <button class="btn-download" title="Download as CSV"
                  @click="downloadCsv(['Health Facility', 'Total Claims', 'Amount Requested', 'Amount Vetted', 'Amount Approved'], providers.map(function (p) { return [p.agency_name, p.claims_count, p.total_cost, p.total_vetted_price, p.total_verified_price]; }), 'claims-provider-breakdown')">
                  <i class="fe fe-download"></i>
                </button>
              </div>
            </div>

            <div class="table-responsive" ref="providersTableRef">
              <table class="table align-td-middle mb-0">
                <thead style="background: #f8fafc;">
                  <tr>
                    <th class="table-th">#</th>
                    <th class="table-th">Health Facility</th>
                    <th class="table-th text-center">Total Claims</th>
                    <th class="table-th">Amount Requested</th>
                    <th class="table-th">Amount Vetted</th>
                    <th class="table-th">Amount Approved</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(provider, index) in providers" :key="provider.id || index" class="table-row">
                    <td class="px-4 py-3 text-muted small">{{ index + 1 }}</td>
                    <td class="py-3 font-weight-bold" style="font-size: 0.86rem;">
                      {{ provider.agency_name || '—' }}
                    </td>
                    <td class="py-3 text-center">
                      <span class="badge badge-soft-info" style="border-radius: 20px; padding: 4px 12px;">
                        {{ provider.claims_count | numeral(0, 0) }}
                      </span>
                    </td>
                    <td class="py-3" style="font-size: 0.86rem;">
                      ₦{{ provider.total_cost | numeral(0, 0) }}
                    </td>
                    <td class="py-3" style="font-size: 0.86rem;">
                      ₦{{ provider.total_vetted_price | numeral(0, 0) }}
                    </td>
                    <td class="py-3 font-weight-bold text-success" style="font-size: 0.86rem;">
                      ₦{{ provider.total_verified_price | numeral(0, 0) }}
                    </td>
                  </tr>
                  <tr v-if="!providers || !providers.length">
                    <td colspan="6" class="text-center py-5 text-muted">
                      <i class="fe fe-inbox" style="font-size: 2rem; opacity: 0.3;"></i>
                      <p class="mt-2 mb-0">Apply filters and click Get Statistics</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ── Top Vetters & Verifiers (claims.checked_by_id / verified_by_id) ── -->
          <div class="card shadow-sm table-card mt-4">
            <div class="form-card-header">
              <div class="section-number">
                <i class="fe fe-users" style="font-size: 12px;"></i>
              </div>
              <h6 class="mb-0 font-weight-bold text-white">Top Vetters & Verifiers</h6>
              <div class="card-download-tools card-download-tools--dark ml-auto">
                <button class="btn-download" title="Download as image" @click="downloadElementImage('actorsRef', 'claims-top-vetters-verifiers')">
                  <i class="fe fe-image"></i>
                </button>
                <button class="btn-download" title="Download as CSV"
                  @click="downloadCsv(['Role', 'Staff', 'Job Title', 'Claims'], actors.vetters.map(function (u) { return ['Vetter', u.name || ('User ' + u.user_id), u.job_title || '', u.count]; }).concat(actors.verifiers.map(function (u) { return ['Verifier', u.name || ('User ' + u.user_id), u.job_title || '', u.count]; })), 'claims-top-vetters-verifiers')">
                  <i class="fe fe-download"></i>
                </button>
              </div>
            </div>
            <div class="card-body p-0" ref="actorsRef">
              <div class="row no-gutters">
                <div class="col-md-6">
                  <h6 class="small font-weight-bold px-3 pt-3">Top Vetters</h6>
                  <div class="table-responsive">
                    <table class="table align-td-middle mb-0">
                      <thead style="background: #f8fafc;">
                        <tr>
                          <th class="table-th">#</th>
                          <th class="table-th">Staff</th>
                          <th class="table-th text-center">Claims Vetted</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(u, idx) in actors.vetters" :key="u.user_id || idx" class="table-row">
                          <td class="px-4 py-3 text-muted small">{{ idx + 1 }}</td>
                          <td class="py-3 font-weight-bold" style="font-size: 0.86rem;">
                            {{ u.name || ('User ' + u.user_id) }}
                            <small v-if="u.job_title" class="d-block text-muted font-weight-normal">{{ u.job_title }}</small>
                          </td>
                          <td class="py-3 text-center">
                            <span class="badge badge-soft-info" style="border-radius: 20px; padding: 4px 12px;">
                              {{ u.count | numeral(0, 0) }}
                            </span>
                          </td>
                        </tr>
                        <tr v-if="!actors.vetters.length">
                          <td colspan="3" class="text-center py-4 text-muted small">No vetting activity for the period</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="col-md-6">
                  <h6 class="small font-weight-bold px-3 pt-3">Top Verifiers</h6>
                  <div class="table-responsive">
                    <table class="table align-td-middle mb-0">
                      <thead style="background: #f8fafc;">
                        <tr>
                          <th class="table-th">#</th>
                          <th class="table-th">Staff</th>
                          <th class="table-th text-center">Claims Verified</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(u, idx) in actors.verifiers" :key="u.user_id || idx" class="table-row">
                          <td class="px-4 py-3 text-muted small">{{ idx + 1 }}</td>
                          <td class="py-3 font-weight-bold" style="font-size: 0.86rem;">
                            {{ u.name || ('User ' + u.user_id) }}
                            <small v-if="u.job_title" class="d-block text-muted font-weight-normal">{{ u.job_title }}</small>
                          </td>
                          <td class="py-3 text-center">
                            <span class="badge badge-soft-info" style="border-radius: 20px; padding: 4px 12px;">
                              {{ u.count | numeral(0, 0) }}
                            </span>
                          </td>
                        </tr>
                        <tr v-if="!actors.verifiers.length">
                          <td colspan="3" class="text-center py-4 text-muted small">No verification activity for the period</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import TodayTasks from "@/components/TodayTasks.vue";
import ClaimItemsStats from "@/views/analytics/components/ClaimItemsStats.vue";
import ClaimAnalysis from "@/views/analytics/components/ClaimAnalysis.vue";
import ClaimsDate from "@/views/shis/components/ClaimsbyDate.vue";
import ClaimsDiagnosesChart from "@/views/shis/components/ClaimsDiagnosesChart.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import SectorsJson from "@/jsons/sectors.json";
import moment from "moment";
moment().format();

export default {
  components: { TodayTasks, ClaimItemsStats, ClaimAnalysis, Navbar, ClaimsDate, ClaimsDiagnosesChart, Loading },
  data() {
    return {
        itemsRefresh: 0,
      user: null,
      filterparams: false,
      isLoading: false,
      fullPage: true,
      from: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
      to: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      providers: [
        { claims_count: 0, total_cost: 0, total_vetted_price: 0, total_verified_price: 0 },
      ],
      stats: "",
      tpas: "",
      claim_status: "",
      org_id: "",
      provider_id: "",
      sector: "",
      // Top vetters / verifiers (claims/stats-actors)
      actors: { vetters: [], verifiers: [] },
      sectors: SectorsJson,
      // facility type of the claim's facility: users.phc_type (level of care) and users.phc_general (ownership)
      phc_type: "",
      phc_general: "",
      careLevels: ["Primary", "Secondary", "Tertiary", "Telemedicine"],
      provider_fields: {
        "Provider Name": "agency_name",
        "Program ": "sector",
        "Total  Claims": "claims_count",
        "Request  Amount": "total_cost",
        "Vetted  Amount": "total_vetted_price",
        "Verified  Amount": "total_verified_price",
      },
      json_meta: [[{ key: "charset", value: "utf-8" }]],

      // Diagnoses chart
      diagnosisChartLoading: false,
      diagnosisCategories: [],
      diagnosisSeries: [{ name: "Claims", data: [] }],
    };
  },
  computed: {
    lastProviderClaimsCount() {
      if (!Array.isArray(this.providers) || this.providers.length === 0) return 0;
      return Number(this.providers[this.providers.length - 1].claims_count) || 0;
    },
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
  methods: {
    facilityTypeParams() {
      return { phc_type: this.phc_type || undefined, phc_general: this.phc_general || undefined };
    },
    runAll() {
      this.itemsRefresh++;
      this.getStats();
      this.getDiagnosesChart();
    },
    getClaims(format = "json") {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.isLoading = true;
      this.axios
        .get(`/api/v1/auth/claims/407465`, {
          params: {
            format,
            per_page: 50000,
            claim_status: this.claim_status,
            claim_unique_id: this.claim_unique_id,
            authorization_code: this.authorization_code,
            id_card_number: this.id_card_number,
            provider_id:
              this.user.type == "provider_employee"
                ? this.user.institutional_id
                : this.provider_id && this.provider_id.id,
            org_id: this.org_id,
            from: this.from,
            to: this.to,
            sector: this.sector,
            ...this.facilityTypeParams(),
          },
        })
        .then((response) => {
          if (format === "csv") {
            const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `claims_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            this.$toasted.success("CSV downloaded successfully", { position: "top-center", duration: 3000 });
            this.isLoading = false;
            return;
          }
          if (!this.providers || this.providers == "") this.providers = response.data.providers;
          this.claims = response.data;
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    getProviders() {
      this.axios
        .get(`/api/v1/auth/providerAgency/407465`)
        .then((response) => { this.providers = response.data.data; })
        .catch((error) => { console.error(error); });
    },
    getStats() {
      this.getActors();
      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/claims/statistics`, {
          agency_id: 407465,
          sector: this.sector,
          from: this.from,
          to: this.to,
          ...this.facilityTypeParams(),
        })
        .then((response) => {
          this.providers = response.data.data
            .filter((item) => item.total_cost > 0)
            .map((item) => ({ ...item, sector: this.sector || "All" }));
          this.isLoading = false;
          this.$toasted.success("Queried Successfully!", { position: "top-left", duration: 5000 });
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    getActors() {
      this.axios
        .get(`/api/v1/auth/claims/stats-actors`, {
          params: { agency_id: 407465, sector: this.sector, from: this.from, to: this.to, ...this.facilityTypeParams() },
        })
        .then((response) => {
          const data = response.data || {};
          this.actors = {
            vetters: Array.isArray(data.vetters) ? data.vetters : [],
            verifiers: Array.isArray(data.verifiers) ? data.verifiers : [],
          };
        })
        .catch((error) => { console.error("Failed to load vetters/verifiers", error); });
    },
    getDiagnosesChart() {
      this.diagnosisChartLoading = true;
      this.axios
        .post(`/api/v1/auth/claims/statistics/diagnoses`, {
          agency_id: 407465,
          sector: this.sector,
          from: this.from,
          to: this.to,
          ...this.facilityTypeParams(),
        })
        .then((response) => {
          const raw = response.data.data || [];

          const sorted = raw
            .slice()
            .sort((a, b) => Number(b.total_claims || 0) - Number(a.total_claims || 0))
            .slice(0, 30);

          this.diagnosisCategories = sorted.map((d) => d.diagnosis_name || "Unknown");
          this.diagnosisSeries = [{
            name: "Claims",
            data: sorted.map((d) => Number(d.total_claims || 0)),
          }];
          this.diagnosisChartLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.diagnosisChartLoading = false;
        });
    },
    getDiagnosesStats() {
      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/claims/statistics/diagnoses`, {
          format: "csv",
          agency_id: 407465,
          sector: this.sector,
          from: this.from,
          to: this.to,
          ...this.facilityTypeParams(),
        }, { responseType: "blob" })
        .then((response) => {
          const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `diagnoses_statistics_${new Date().toISOString().slice(0, 10)}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.isLoading = false;
          this.$toasted.success("Diagnoses CSV downloaded successfully", { position: "top-left", duration: 5000 });
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    clearField() {
      this.phc_type = "";
      this.phc_general = "";
      this.claim_status = "";
      this.claim_unique_id = "";
      this.authorization_code = "";
      this.id_card_number = "";
      this.provider_id = "";
      this.org_id = "";
      this.sector = "";
      this.providers = "";
    },
    async downloadElementImage(refName, filename) {
      try {
        const el = this.$refs[refName];
        const dataUrl = await this.$html2canvas(el, { type: "dataURL" });
        const link = document.createElement("a");
        link.href = dataUrl;
        link.setAttribute("download", `${filename}.png`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error(error);
        this.$toasted.error("Could not generate image.", { position: "top-center", duration: 3000 });
      }
    },
    downloadCsv(headers, rows, filename) {
      const escapeCell = function (value) {
        const str = value === null || value === undefined ? "" : String(value);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };
      const lines = [headers, ...rows].map(function (row) { return row.map(escapeCell).join(","); });
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${filename}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    downloadSummaryCsv() {
      const last = this.providers && this.providers.length ? this.providers[this.providers.length - 1] : {};
      const headers = ["Metric", "Value"];
      const rows = [
        ["Total Claims", this.lastProviderClaimsCount],
        ["Amount Requested", last.total_cost || 0],
        ["Amount Approved", last.total_verified_price || 0],
      ];
      this.downloadCsv(headers, rows, "claims-summary");
    },
  },
  created() {
    this.getStats();
    this.getDiagnosesChart();
  },
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  padding-bottom: 40px;
}

.header-icon-wrap {
  width: 48px; height: 48px;
  background: rgba(255,255,255,0.15);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

.filter-toggle-btn {
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  color: #fff;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 7px 16px;
  transition: background 0.15s;
}
.filter-toggle-btn:hover {
  background: rgba(255,255,255,0.25);
  color: #fff;
}

/* Stat cards */
.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  margin-bottom: 16px;
}

.stat-icon-wrap {
  width: 48px; height: 48px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.stat-label {
  font-size: 0.72rem; text-transform: uppercase;
  letter-spacing: 0.05em; color: #94a3b8;
  margin-bottom: 2px; font-weight: 600;
}

.stat-value {
  font-size: 1.35rem; font-weight: 700;
  color: #1e293b; margin-bottom: 0;
  line-height: 1.2;
}

/* Cards */
.form-card, .table-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.form-card-header {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}

.section-number {
  width: 28px; height: 28px;
  background: rgba(255,255,255,0.2);
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0;
}

.table-th {
  font-size: 0.73rem; text-transform: uppercase; letter-spacing: 0.04em;
  color: #64748b; font-weight: 600;
  border-bottom: 2px solid #e2e8f0; padding: 12px 16px;
}

.table-row:hover { background: #f8fafc; }

.btn-submit {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border: none; color: #fff;
  border-radius: 8px; font-weight: 600; padding: 8px 20px;
}

.btn-submit:hover { opacity: 0.9; color: #fff; }

.form-control:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 0.15rem rgba(37,99,235,0.15);
}

.card-download-tools {
  display: flex;
  gap: 4px;
}

.card-download-tools--dark {
  margin-left: auto;
}

.btn-download {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.btn-download:hover {
  background: #f1f5f9;
  color: #1e3a5f;
}

.card-download-tools--dark .btn-download {
  border-color: rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
.card-download-tools--dark .btn-download:hover {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}
</style>
